import { ReactElement } from "react";
import { FormFieldProps, FormHolder, NullableFormValue, ValueConverter } from "./index";
import StateHolder from "./StateHolder";
import { defaultValueManager } from "./valuemanager";
import { defaultErrorManager } from "./errormanager";

export function getError<T, XT>( props: FormFieldProps<T, XT> ): string | ReactElement | null {
	const { error } = props;
	if ( error ) return error;
	return readError( props ) as string;
}

export function isViewError<T, XT>( props: FormFieldProps<T, XT> ) {
	return getError( props ) != null;
}


function getConverters<T, XT>( props: FormFieldProps<T, XT> ): ValueConverter<T, XT>[] | undefined {
	const { valueConverter } = props;
	if ( valueConverter != null ) {
		return Array.isArray( valueConverter ) ? valueConverter : [ valueConverter ];
	}
	return undefined;
}

export function convertReadValue<T, XT>( props: FormFieldProps<T, XT>, value: NullableFormValue<T, XT> ): NullableFormValue<T, XT> {
	const valueConverter = getConverters( props );
	let v = value;
	if ( valueConverter != null ) {
		for ( const converter of valueConverter ) {
			v = converter.fromData( v, props );
		}
	}
	return v;
}

export function convertWriteValue<T, XT>( props: FormFieldProps<T, XT>, value: NullableFormValue<T, XT> ): NullableFormValue<T, XT> {
	const valueConverter = getConverters( props );
	let v = value;
	if ( valueConverter != null ) {
		for ( const converter of valueConverter ) {
			v = converter.toData( v, props );
		}
	}
	return v;
}

export function readValue<T, XT>( props: FormFieldProps<T, XT> ) {
	let { id, valueManager } = props;
	if ( valueManager == null ) valueManager = defaultValueManager;
	let read = valueManager?.read( props, id );
	read = convertReadValue( props, read );
	return read;
}

export function writeValue<T, XT>( props: FormFieldProps<T, XT>, value: NullableFormValue<T, XT>, callback?: () => void ) {
	let { id, valueManager } = props;
	if ( valueManager == null ) valueManager = defaultValueManager;
	writeError( props, undefined );
	value = convertWriteValue( props, value );
	return valueManager.write( props, id, value, callback );
}

export function readError<T, XT>( props: FormFieldProps<T, XT> ) {
	let { id, errorManager, error, valueValidators } = props;
	if ( error ) return error;
	if ( errorManager == null ) errorManager = defaultErrorManager;
	let e = errorManager?.readError( props, id );
	if ( e ) return e;
	if ( valueValidators ) {
		const validators = Array.isArray( valueValidators ) ? valueValidators : [ valueValidators ];
		for ( const validator of validators ) {
			let ve = validator( props );
			if ( ve ) return ve;
		}
	}
	return e;
}

export function writeError<T, XT>( props: FormFieldProps<T, XT>, error: string | undefined, callback?: () => void ) {
	let { id, errorManager } = props;
	if ( errorManager == null ) errorManager = defaultErrorManager;
	return errorManager?.writeError( props, id, error, callback );
}

export function getHolder<T, XT>( props: FormFieldProps<T, XT> ): FormHolder<T, XT> {
	const { holder } = props;
	if ( holder?.writeValue === undefined ) {
		return StateHolder.buildStateHolder( holder as any );
	}
	return holder;
}
